/**
 * Parses a date string in the format "DD/MM/YYYYTHH:MM" or standard Date strings.
 * @param {string|Date} dateStr - The date string or Date object to parse.
 * @returns {Date} The parsed Date object.
 */
import { format, parse } from 'date-fns';

export function parseDate(dateStr) {
  if (dateStr instanceof Date) return dateStr;

  if (typeof dateStr === 'string' && dateStr.includes('/') && dateStr.includes('T')) {
    const [datePart, timePart] = dateStr.split('T');
    const [day, month, year] = datePart.split('/');
    const [hours, minutes] = timePart.split(':');
    return new Date(year, month - 1, day, hours, minutes); // month is 0-based in Date constructor
  }

  // Fallback to standard Date parsing
  return new Date(dateStr);
}

export function normalizeLocalDateTimeString(dateStr) {
  if (dateStr instanceof Date) {
    return format(dateStr, "yyyy-MM-dd'T'HH:mm");
  }

  if (typeof dateStr !== 'string') {
    return null;
  }

  const trimmed = dateStr.trim();
  if (!trimmed) {
    return null;
  }

  const customPatterns = [
    'dd/MM/yy HH:mm',
    'dd/MM/yyyy HH:mm',
    "dd/MM/yy'T'HH:mm",
    "dd/MM/yyyy'T'HH:mm",
    "yyyy-MM-dd'T'HH:mm",
    'yyyy-MM-dd HH:mm',
    "yyyy-MM-dd'T'HH:mm:ss",
    'yyyy-MM-dd HH:mm:ss'
  ];

  for (const pattern of customPatterns) {
    try {
      const parsed = parse(trimmed, pattern, new Date());
      if (!Number.isNaN(parsed.getTime())) {
        return format(parsed, "yyyy-MM-dd'T'HH:mm");
      }
    } catch (error) {
      // Ignore parse failures and try the next pattern.
    }
  }

  const stripped = trimmed
    .replace(/Z$/i, '')
    .replace(/[+-]\d{2}:?\d{2}$/, '')
    .replace(/[+-]\d{2}$/, '');

  const parsed = new Date(stripped);
  if (!Number.isNaN(parsed.getTime())) {
    return format(parsed, "yyyy-MM-dd'T'HH:mm");
  }

  return null;
}

/**
 * Parses a date-time string as if it were local time at the rental destination,
 * even when the API includes a UTC timezone suffix such as Z or +00:00.
 * This matches the app's search param behavior, where date-time strings are
 * treated as local time rather than converted from UTC.
 */
export function parseAsLocalDateTime(dateStr) {
  if (dateStr instanceof Date) return dateStr;

  if (typeof dateStr !== 'string') return new Date(dateStr);

  const trimmed = dateStr.trim();
  if (!trimmed) return new Date('');

  const normalized = normalizeLocalDateTimeString(trimmed);
  if (!normalized) {
    return new Date(trimmed);
  }

  return new Date(normalized);
}